import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { Share2, Copy, Facebook, Twitter, Mail, MessageCircle } from "lucide-react";
import { formatPriceDisplay } from '@/utils/currency';
import { getPropertyUrl } from '@/utils/slugify';

interface PropertyShareProps {
  property: any;
  className?: string;
}

// Social media icons
const FacebookIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
    />
  </svg>
);

const TwitterIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
    />
  </svg>
);

const WhatsAppIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.149-.672.149-.198.297-.768.967-.941 1.166-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.148-.173.198-.297.297-.495.099-.198.05-.371-.025-.52-.075-.149-.672-1.611-.921-2.206-.242-.579-.487-.5-.672-.51l-.572-.01c-.198 0-.52.074-.793.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.262.489 1.694.626.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.571-.347m-4.421 6.421h-.005a8.658 8.658 0 01-4.688-1.366l-.337-.2-3.48.912.929-3.392-.219-.348a8.682 8.682 0 01-1.337-4.637c.001-4.798 3.908-8.703 8.709-8.703 2.324 0 4.507.905 6.147 2.544a8.561 8.561 0 012.557 6.133c-.003 4.798-3.908 8.703-8.706 8.703M20.52 3.449A11.815 11.815 0 0012.047 0C5.495 0 .273 5.223.27 11.777c0 2.077.543 4.108 1.58 5.9L0 24l6.4-1.679a11.82 11.82 0 005.651 1.439h.005c6.554 0 11.777-5.223 11.78-11.777a11.72 11.72 0 00-3.316-8.534z"
    />
  </svg>
);

export const PropertyShare = ({ property, className }: PropertyShareProps) => {
  const [open, setOpen] = useState(false);
  
  const propertyUrl = `${window.location.origin}${getPropertyUrl(property)}`;
  
  const shareText = `Check out this property: ${property.title}
📍 ${property.location}, ${property.city}
💰 ${formatPriceDisplay({ price: property.price })}
🏗️ ${property.property_type.replace('_', ' ')}`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(propertyUrl);
      toast({
        title: "Link Copied!",
        description: "Property link copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy link to clipboard",
        variant: "destructive",
      });
    }
  };

  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(propertyUrl)}`;
    window.open(url, '_blank', 'width=555,height=333');
  };

  const shareOnTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(propertyUrl)}`;
    window.open(url, '_blank', 'width=555,height=333');
  };

  const shareOnWhatsApp = () => {
    const message = `${shareText}\n\n${propertyUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const shareViaEmail = () => {
    const subject = `Property: ${property.title}`;
    const body = `${shareText}\n\nView full details: ${propertyUrl}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className={className} aria-label="Share property">
          <Share2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share Property</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Copy Link */}
          <div className="space-y-2">
            <Label htmlFor="link">Property Link</Label>
            <div className="flex space-x-2">
              <Input id="link" value={propertyUrl} readOnly />
              <Button size="sm" onClick={copyToClipboard}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Separator />

          {/* Social Media Buttons */}
          <div className="space-y-3">
            <Label>Share on social media</Label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={shareOnFacebook}
                className="w-full justify-start"
              >
                <FacebookIcon className="h-5 w-5 mr-2 text-blue-600" />
                Facebook
              </Button>
              <Button
                variant="outline"
                onClick={shareOnTwitter}
                className="w-full justify-start"
              >
                <TwitterIcon className="h-5 w-5 mr-2 text-gray-900" />
                Twitter
              </Button>
              <Button
                variant="outline"
                onClick={shareOnWhatsApp}
                className="w-full justify-start"
              >
                <WhatsAppIcon className="h-5 w-5 mr-2 text-green-600" />
                WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={shareViaEmail}
                className="w-full justify-start"
              >
                <Mail className="h-5 w-5 mr-2 text-gray-600" />
                Email
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};